import type { AdminGame, GameInput, GameProvider, StorePlatform } from "@lucky-try/contracts";
import { GLOBAL_MARKET } from "@lucky-try/shared";

export const STORE_PLATFORMS: StorePlatform[] = ["app_store", "google_play", "crazy_games", "apk"];

/** Flattened variant row: every provider field is present so the editor can switch provider in place. */
export interface VariantForm {
  id?: number;
  market: string;
  provider: GameProvider;
  isEnabled: boolean;
  url: string;
  projectId: string;
  platformKey: string;
  assetId: string;
  directory: string;
  version: string;
}

export interface StoreLinkForm {
  platform: StorePlatform;
  url: string;
}

/** Scalar fields live in react-hook-form; repeatables are plain state so the provider union stays explicit. */
export interface ScalarValues {
  slug: string;
  name: string;
  status: "draft" | "published" | "archived";
  categoryId: number;
  author: string;
  releaseDate: string;
  briefDescription: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  orientation: "landscape" | "portrait";
  backgroundColor: string;
  ratingValue: string;
  ratingCount: string;
  isPopular: boolean;
  isNew: boolean;
  isOriginal: boolean;
  sortOrder: number;
  adsenseEligible: boolean;
  disableAdblockNotice: boolean;
}

export const scalarDefaults: ScalarValues = {
  slug: "",
  name: "",
  status: "draft",
  categoryId: 0,
  author: "",
  releaseDate: "",
  briefDescription: "",
  description: "",
  seoTitle: "",
  seoDescription: "",
  orientation: "landscape",
  backgroundColor: "",
  ratingValue: "",
  ratingCount: "",
  isPopular: false,
  isNew: false,
  isOriginal: false,
  sortOrder: 0,
  adsenseEligible: true,
  disableAdblockNotice: false,
};

export const emptyVariant = (): VariantForm => ({
  market: GLOBAL_MARKET,
  provider: "gamedistribution",
  isEnabled: true,
  url: "",
  projectId: "",
  platformKey: "",
  assetId: "",
  directory: "",
  version: "",
});

/** Markets are stored as `global` or an upper-case ISO country code. */
export function normalizeMarketInput(value: string): string {
  const trimmed = value.trim();
  return trimmed.toLowerCase() === GLOBAL_MARKET ? GLOBAL_MARKET : trimmed.toUpperCase();
}

export function scalarsFrom(game: AdminGame): ScalarValues {
  return {
    slug: game.slug,
    name: game.name,
    status: game.status,
    categoryId: game.categoryId,
    author: game.author ?? "",
    releaseDate: game.releaseDate ?? "",
    briefDescription: game.briefDescription ?? "",
    description: game.description ?? "",
    seoTitle: game.seoTitle ?? "",
    seoDescription: game.seoDescription ?? "",
    orientation: game.orientation,
    backgroundColor: game.backgroundColor ?? "",
    ratingValue: game.ratingValue === null ? "" : String(game.ratingValue),
    ratingCount: game.ratingCount === null ? "" : String(game.ratingCount),
    isPopular: game.isPopular,
    isNew: game.isNew,
    isOriginal: game.isOriginal,
    sortOrder: game.sortOrder,
    adsenseEligible: game.adsenseEligible,
    disableAdblockNotice: game.disableAdblockNotice,
  };
}

export function variantsFrom(game: AdminGame): VariantForm[] {
  return game.variants.map((variant) => ({
    id: variant.id,
    market: variant.market,
    provider: variant.provider,
    isEnabled: variant.isEnabled,
    url: variant.url ?? "",
    projectId: variant.projectId ?? "",
    platformKey: typeof variant.config.platformKey === "string" ? variant.config.platformKey : "",
    assetId: typeof variant.config.assetId === "string" ? variant.config.assetId : "",
    directory: variant.directory ?? "",
    version: variant.version ?? "",
  }));
}

export function toVariantInput(variant: VariantForm): Record<string, unknown> {
  const base = {
    ...(variant.id ? { id: variant.id } : {}),
    market: variant.market,
    isEnabled: variant.isEnabled,
  };
  switch (variant.provider) {
    case "gamedistribution":
      return {
        ...base,
        provider: "gamedistribution",
        url: variant.url,
        config: variant.assetId ? { assetId: variant.assetId } : {},
      };
    case "gamepush":
      return {
        ...base,
        provider: "gamepush",
        url: variant.url,
        projectId: variant.projectId,
        config: variant.platformKey ? { platformKey: variant.platformKey } : {},
      };
    case "self_hosted":
      return {
        ...base,
        provider: "self_hosted",
        directory: variant.directory,
        version: variant.version,
        config: {},
      };
    default:
      return { ...base, provider: "external", url: variant.url, config: {} };
  }
}

const optionalText = (value: string) => (value.trim() === "" ? null : value.trim());
const optionalNumber = (value: string) => (value.trim() === "" ? null : Number(value));

export interface GameFormExtras {
  iconAssetId: string | null;
  previewAssetId: string | null;
  thumbnailAssetId: string | null;
  storeLinks: StoreLinkForm[];
  variants: VariantForm[];
}

/** Builds the API payload; the shared contract stays the single source of validation truth. */
export function toGameInput(values: ScalarValues, extras: GameFormExtras): GameInput {
  return {
    slug: values.slug,
    name: values.name,
    status: values.status,
    categoryId: Number(values.categoryId),
    author: optionalText(values.author),
    releaseDate: optionalText(values.releaseDate),
    briefDescription: optionalText(values.briefDescription),
    description: optionalText(values.description),
    seoTitle: optionalText(values.seoTitle),
    seoDescription: optionalText(values.seoDescription),
    orientation: values.orientation,
    backgroundColor: optionalText(values.backgroundColor),
    ratingValue: optionalNumber(values.ratingValue),
    ratingCount: optionalNumber(values.ratingCount),
    isPopular: values.isPopular,
    isNew: values.isNew,
    isOriginal: values.isOriginal,
    sortOrder: Number(values.sortOrder),
    adsenseEligible: values.adsenseEligible,
    disableAdblockNotice: values.disableAdblockNotice,
    iconAssetId: extras.iconAssetId,
    previewAssetId: extras.previewAssetId,
    thumbnailAssetId: extras.thumbnailAssetId,
    storeLinks: extras.storeLinks,
    variants: extras.variants.map(toVariantInput),
  } as GameInput;
}
